import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Status pills. Each tone pairs a soft ground with the matching text token, and
 * both halves are held to 4.5:1 by the token audit in both themes — status text
 * on a tinted ground is the classic place a design system quietly fails AA.
 *
 * Colour is never the only carrier: a tone always accompanies a word.
 */
const badge = cva("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium", {
  variants: {
    tone: {
      neutral: "bg-[var(--surface-3)] text-[var(--text-muted)]",
      accent: "bg-[var(--accent-soft)] text-[var(--accent-ink)]",
      success: "bg-[var(--success-soft)] text-[var(--success)]",
      warning: "bg-[var(--warning-soft)] text-[var(--warning)]",
      danger: "bg-[var(--danger-soft)] text-[var(--danger)]",
      info: "bg-[var(--info-soft)] text-[var(--info)]",
    },
  },
  defaultVariants: { tone: "neutral" },
});

type BadgeProps = React.ComponentPropsWithoutRef<"span"> & VariantProps<typeof badge>;

export function Badge({ tone, className, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone }), className)} {...props} />;
}

/**
 * Replaces the hand-rolled `h-2 rounded-full bg-neutral-200` track with an inline
 * width style that appears on the dashboard and in the player. Exposes the value
 * to assistive tech, which the div version did not.
 */
type ProgressMeterProps = {
  value: number;
  max?: number;
  label: string;
  className?: string;
  "data-testid"?: string;
};

export function ProgressMeter({
  value,
  max = 100,
  label,
  className,
  ...props
}: ProgressMeterProps) {
  const pct = Math.max(0, Math.min(100, max === 0 ? 0 : (value / max) * 100));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn("h-2 w-full overflow-hidden rounded-full bg-[var(--surface-3)]", className)}
      {...props}
    >
      <div
        className="h-full rounded-full bg-[var(--accent-strong)] transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
